import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, Input } from '../components';
import authService from '../api/services/authService';

// --- Animations ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const bubbleFloat = keyframes`
  0% {
    transform: translateY(100%) scale(0);
    opacity: 0;
  }
  10% { opacity: 1; }
  100% {
    transform: translateY(-100vh) scale(1);
    opacity: 0;
  }
`;

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.15); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.4; }
`;

// --- Styled Components ---

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f1229 0%, #232562 30%, #2d3070 55%, #3a6b8a 75%, #59caee 100%);
  background-size: 300% 300%;
  animation: ${gradientShift} 12s ease infinite;
  padding: ${p => p.theme.spacing.xl};
  position: relative;
  overflow: hidden;
`;

const Bubble = styled.div`
  position: absolute;
  bottom: -20px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(89, 202, 238, 0.2), rgba(89, 202, 238, 0.03));
  border: 1px solid rgba(89, 202, 238, 0.1);
  animation: ${bubbleFloat} ${p => p.$duration}s linear infinite;
  animation-delay: ${p => p.$delay}s;
  left: ${p => p.$left}%;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  pointer-events: none;
`;

const FloatingBubbles = () => {
  const bubbles = [
    { $size: 10, $left: 12, $duration: 15, $delay: 0 },
    { $size: 16, $left: 35, $duration: 12, $delay: 3 },
    { $size: 7, $left: 58, $duration: 18, $delay: 1 },
    { $size: 20, $left: 78, $duration: 14, $delay: 5 },
    { $size: 9, $left: 92, $duration: 21, $delay: 2 },
    { $size: 13, $left: 22, $duration: 16, $delay: 7 },
  ];
  return <>{bubbles.map((b, i) => <Bubble key={i} {...b} />)}</>;
};

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 40px;
  position: relative;
  z-index: 10;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 480px) {
    padding: 28px 24px;
  }
`;

const IconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(89, 202, 238, 0.12);
  border: 1px solid rgba(89, 202, 238, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: ${p => p.theme.spacing.lg};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 50%;
    border: 1px solid rgba(89, 202, 238, 0.15);
    animation: ${pulseRing} 3s ease-in-out infinite;
  }
`;

const LogoImage = styled.img`
  width: 110px;
  height: auto;
  margin-bottom: ${p => p.theme.spacing.lg};
  filter: brightness(0) invert(1);
  opacity: 0.9;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.25);
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
  color: #ff7b8a;
  text-align: center;
  font-size: ${p => p.theme.typography.sizes?.small};
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: rgba(89, 202, 238, 0.9);
  cursor: pointer;
  font-size: ${p => p.theme.typography.sizes?.small};
  padding: 0;
  margin-top: ${p => p.theme.spacing.md};
  align-self: center;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const PasswordHint = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-top: -8px;
`;

// --- Component ---

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError('Password must be at least 8 characters with at least one uppercase letter and one number');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, newPassword);
      toast.success('Password has been reset successfully! Please sign in.');
      navigate('/login');
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to reset password. The link may have expired.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PageContainer theme={theme}>
        <FloatingBubbles />
        <Card theme={theme}>
          <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" />
          <IconCircle theme={theme}><AlertTriangle size={28} color="white" /></IconCircle>
          <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.md, color: '#fff', fontWeight: 700 }}>
            Invalid Reset Link
          </ThemedText>
          <ThemedText type="default" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: theme.spacing.xl, fontSize: '14px', lineHeight: '1.6' }}>
            This password reset link is invalid or has expired. Please request a new one.
          </ThemedText>
          <BackLink theme={theme} type="button" onClick={() => navigate('/forgot-password')}>
            Request New Reset Link
          </BackLink>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer theme={theme}>
      <FloatingBubbles />
      <Card theme={theme}>
        <LogoImage src="/img/aqrate-logo.png" alt="aQRate Logo" />
        <IconCircle theme={theme}><KeyRound size={28} color="white" /></IconCircle>
        <ThemedText type="title" style={{ fontSize: '24px', marginBottom: theme.spacing.sm, color: '#fff', fontWeight: 700 }}>
          Set New Password
        </ThemedText>
        <ThemedText type="default" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: theme.spacing.xl, fontSize: '14px', lineHeight: '1.6' }}>
          Choose a strong password for your account.
        </ThemedText>

        <Form onSubmit={handleSubmit}>
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            required
          />

          <PasswordHint>
            Min 8 characters, 1 uppercase letter, 1 number
          </PasswordHint>

          <Input
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            required
          />

          {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}

          <Button type="submit" size="lg" disabled={loading || !newPassword || !confirmPassword}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>

          <BackLink theme={theme} type="button" onClick={() => navigate('/login')}>
            Back to Sign In
          </BackLink>
        </Form>
      </Card>
    </PageContainer>
  );
};
